import { LitElement, html, TemplateResult, nothing } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { translateFn } from '../../../../utils/translator.helper';
import { FontSizes, FontWeights } from '../../../../types/common/types';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import { GzLandingPageCompanyType } from '../../../../landingPage/types';
import { getLandingPageReference } from '../../../../landingPage/utils';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

@customElement('gz-go-to-feature-widget')
export class GoToFeatureWebComponent extends LitElement {
  @property({ type: String, reflect: true })
  featureTitle: string;

  @property({ type: String, reflect: true })
  featureIcon: string;

  @property({ type: String, reflect: true })
  iconSize: number;

  @property({ type: String, reflect: true })
  featureIconType: string;

  @property({ type: String, reflect: true })
  featureSummary: string;

  @property({ type: String, reflect: true })
  featureId: string;

  @property({ type: Boolean, reflect: true })
  compact: boolean;

  @property({ type: Number })
  companyType: GzLandingPageCompanyType;

  @property({ type: String, reflect: true })
  actionButtonColor: string;

  @property({ type: String, reflect: true })
  actionButtonType: string;

  @property({ type: String, reflect: true })
  actionButtonText: string;

  @property({ type: String, reflect: true })
  actionButtonTooltip: string;

  @property({ type: String, reflect: true })
  actionButtonDisabled = false;

  @property({ type: String, reflect: true })
  featureBodyImage: string;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  render() {
    const isNotAvailable = !this.featureId;
    const classes = {
      compact: Boolean(this.compact),
      'not-available': isNotAvailable,
      'body-image': Boolean(this.featureBodyImage),
    };
    const clickFunction = this.compact ? () => this.goToFeatureBtnAction(this.featureId) : undefined;
    let icon = html``;
    if (this.featureIcon) {
      const image =
        this.featureIconType === 'icon'
          ? html`<gz-icon name="${this.featureIcon}" size="${this.iconSize ?? 24}"></gz-icon>`
          : html`<img src="${this.featureIcon}" alt="" />`;

      icon = html`<div class="icon">${image}</div>`;
    }
    const featureImage = this.featureBodyImage ? html`<img src="${this.featureBodyImage}" alt="" />` : nothing;

    const cardContent = html` <div class="card--content ${classMap(classes)}" @click=${ifDefined(clickFunction)}>
      ${featureImage}
      <div class="card--content-wrap">
        <div class="card--title-wrap">
          ${icon}
          <gz-title
            title="${this.featureTitle}"
            id="go-to-feature-title"
            fontSize=${FontSizes.TITLE}
            fontWeight=${FontWeights.MEDIUM}
            class="card--title"
            >${unsafeHTML(this.featureTitle)}</gz-title
          >
        </div>
        ${this.compact ? nothing : this.renderFeatureSummary()}
        ${this.compact ? nothing : this.renderFeatureActionBtn()}
      </div>
    </div>`;

    return html`
      <div class="card--wrapper">${isNotAvailable ? this.addPermissionTooltip(cardContent) : cardContent}</div>
    `;
  }

  private addPermissionTooltip(element: TemplateResult) {
    return html`<gz-tooltip
      tooltipText="${translateFn('This feature is not available due to insufficient user rights.')}"
    >
      ${element}
    </gz-tooltip>`;
  }

  private renderFeatureSummary(): TemplateResult | undefined {
    if (!this.featureSummary) {
      return;
    }

    return html`<gz-text id="go-to-feature-summary" fontSize=${FontSizes.HEADING_6} class="card--intro"
      >${this.featureSummary}</gz-text
    >`;
  }

  private renderFeatureActionBtn(): TemplateResult | undefined {
    if (!this.featureId) {
      return;
    }

    return html`
      <gz-tooltip tooltipText="${this.actionButtonTooltip}">
        <gz-button
          id="go-to-feature-button"
          ?disabled="${this.actionButtonDisabled}"
          type="${this.actionButtonType ?? 'full'}"
          color="${this.actionButtonColor ?? 'primary'}"
          class="card--action-button"
          @click="${() => this.goToFeatureBtnAction(this.featureId)}"
        >
          ${this.actionButtonText ?? translateFn('Go to feature')}
        </gz-button>
      </gz-tooltip>
    `;
  }

  private goToFeatureBtnAction(featureId: string): void {
    try {
      analytics.trackPageInteraction({
        reference: `${getLandingPageReference(this.companyType)}`,
        asset: featureId,
      });
    } finally {
      this.dispatchEvent(
        new Event(featureId, {
          bubbles: true,
          composed: true,
        })
      );
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-go-to-feature-widget': GoToFeatureWebComponent;
  }
}
